import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from './ui/Icon';
import PastelButton from './ui/PastelButton';

const MemoryCard = ({ memory, index, onCardClick }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Stagger the appearance of memory cards
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, index * 200);

    return () => clearTimeout(timer);
  }, [index]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const truncateText = (text, maxLength = 60) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  return (
    <motion.div
      className={`cursor-pointer ${
        isVisible ? 'opacity-100' : 'opacity-0'
      }`}
      initial={{ opacity: 0, scale: 0.75, y: 16 }}
      animate={{ 
        opacity: isVisible ? 1 : 0, 
        scale: isVisible ? 1 : 0.75, 
        y: isVisible ? 0 : 16 
      }}
      transition={{ duration: 0.6, ease: 'easeOut' }}
      whileHover={{ y: -4, scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      onClick={() => onCardClick(memory)}
    >
      <div className="group relative">
        {/* Glow ring effect */}
        <div className="absolute -inset-1.5 bg-gradient-to-r from-pink-400/25 to-purple-400/25 rounded-full blur-md opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        
        {/* Memory card - premium glass bubble */}
        <div className="relative bg-white/85 backdrop-blur-sm rounded-full p-2 shadow-[0_4px_16px_rgba(0,0,0,0.08)] border border-white/60 transition-shadow duration-300 group-hover:shadow-[0_8px_24px_rgba(0,0,0,0.12)]">
          {/* Inner highlight */}
          <div className="absolute inset-0 rounded-full bg-gradient-to-br from-white/40 via-transparent to-transparent pointer-events-none" />
          
          {/* Circular image */}
          <div className="w-16 h-16 sm:w-20 sm:h-20 relative overflow-hidden rounded-full">
            <img
              src={memory.image}
              alt="Memory"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><rect width="100" height="100" fill="%23f0f0f0"/><text x="50" y="50" text-anchor="middle" dy=".3em" fill="%23999">📷</text></svg>';
              }}
            />
            
            {/* Subtle overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
            
            {/* Click indicator sparkle */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
              <div className="bg-white/90 backdrop-blur-sm rounded-full p-1.5 shadow-sm">
                <Icon name="sparkle" size="xs" color="#7c3aed" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// Memory Edit Modal Component
const MemoryEditModal = ({ memory, isOpen, onClose, onSaveMemory }) => {
  const [editedText, setEditedText] = useState('');
  const [editedDate, setEditedDate] = useState('');
  const [editedImage, setEditedImage] = useState('');
  const [imageFile, setImageFile] = useState(null);

  useEffect(() => {
    if (memory) {
      setEditedText(memory.text || '');
      setEditedDate(memory.date || '');
      setEditedImage(memory.image || '');
      setImageFile(null);
    }
  }, [memory]);

  if (!isOpen || !memory) return null;

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setEditedImage(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    const updatedMemory = {
      ...memory,
      text: editedText,
      date: editedDate,
      image: editedImage,
    };
    onSaveMemory(updatedMemory);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white/90 backdrop-blur-md rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-[0_16px_40px_rgba(0,0,0,0.10)] border border-white/30"
      >
        {/* Inner highlight */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none" />
        
        {/* Header */}
        <div className="relative py-4 px-1.5 sm:py-6 sm:px-5 pb-2 sm:pb-4">
          <motion.button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/60 hover:bg-white/80 backdrop-blur-sm rounded-full p-2.5 transition-colors duration-200 border border-white/40 shadow-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon name="close" size="sm" color="neutral" />
          </motion.button>
          <h3 className="text-xl font-bold text-gray-700 pr-12 flex items-center gap-2">
            <Icon name="settings" size="md" color="primary" />
            Edit Memory
          </h3>
        </div>

        {/* Edit Form */}
        <div className="relative px-1.5 sm:px-5 pb-4 sm:pb-6">
          {/* Image Upload */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
              <Icon name="camera" size="sm" color="primary" />
              Update photo
            </label>
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                id="edit-memory-image-upload"
              />
              <label
                htmlFor="edit-memory-image-upload"
                className="flex items-center justify-center w-full h-36 border-2 border-dashed border-purple-200/60 rounded-2xl cursor-pointer hover:border-purple-300 transition-all duration-200 bg-white/40 backdrop-blur-sm hover:bg-white/50"
              >
                {editedImage ? (
                  <img
                    src={editedImage}
                    alt="Memory preview"
                    className="w-full h-full object-cover rounded-2xl"
                  />
                ) : (
                  <div className="text-center text-purple-500 flex flex-col items-center">
                    <Icon name="camera" size="xl" color="primary" className="mb-2" />
                    <p className="text-sm font-medium">Click to upload new image</p>
                  </div>
                )}
              </label>
            </div>
          </div>

          {/* Memory Text */}
          <div className="mb-5">
            <label className="block text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
              <Icon name="heart" size="sm" color="memories" />
              Memory description
            </label>
            <textarea
              id="edit-memory-description"
              name="edit-memory-description"
              value={editedText}
              onChange={(e) => setEditedText(e.target.value)}
              placeholder="Share what makes this memory special..."
              className="w-full h-28 p-4 bg-white/50 backdrop-blur-sm border border-white/40 rounded-2xl text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-300/50 focus:border-purple-200/50 transition-all duration-200 resize-none text-sm"
            />
          </div>

          {/* Date */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-600 mb-2 flex items-center gap-2">
              <Icon name="star" size="sm" color="bonds" />
              Date
            </label>
            <input
              id="edit-memory-date"
              name="edit-memory-date"
              type="date"
              value={editedDate}
              onChange={(e) => setEditedDate(e.target.value)}
              className="w-full p-3.5 bg-white/50 backdrop-blur-sm border border-white/40 rounded-2xl text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-300/50 focus:border-purple-200/50 transition-all duration-200 text-sm"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            {/* Cancel Button */}
            <motion.button
              onClick={onClose}
              className="flex-1 bg-white/50 backdrop-blur-sm text-gray-600 px-4 py-3 rounded-2xl font-medium border border-white/40 shadow-sm hover:bg-white/70 transition-colors duration-200"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            
            {/* Save Button */}
            <motion.button
              onClick={handleSave}
              className="flex-1 bg-gradient-to-r from-purple-400 to-pink-400 hover:from-purple-500 hover:to-pink-500 text-white px-4 py-3 rounded-2xl font-semibold shadow-md hover:shadow-lg transition-all duration-200"
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Save Changes
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

const MemoryGarden = ({ memories = [], onDeleteMemory, onEditMemory, onUpdateMemory }) => {
  const [windowWidth, setWindowWidth] = useState(typeof window !== 'undefined' ? window.innerWidth : 768);
  const [selectedMemory, setSelectedMemory] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [editingMemory, setEditingMemory] = useState(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [memoryToDelete, setMemoryToDelete] = useState(null);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    if (typeof window !== 'undefined') {
      window.addEventListener('resize', handleResize);
      return () => window.removeEventListener('resize', handleResize);
    }
  }, []);

  // Debug log
  useEffect(() => {
    console.log('MemoryGarden received memories:', memories);
  }, [memories]);

  const handleCardClick = (memory) => {
    setSelectedMemory(memory);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedMemory(null);
  };

  const handleDeleteMemory = (memory) => {
    setMemoryToDelete(memory);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = () => {
    if (onDeleteMemory && memoryToDelete) {
      onDeleteMemory(memoryToDelete);
    }
    setIsConfirmModalOpen(false);
    setMemoryToDelete(null);
    setIsModalOpen(false); // Close the detail modal too
  };

  const handleCancelDelete = () => {
    setIsConfirmModalOpen(false);
    setMemoryToDelete(null);
  };

  const handleEditMemory = (memory) => {
    setEditingMemory(memory);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setEditingMemory(null);
  };

  if (!memories || memories.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md mx-auto"
      >
        <div className="relative bg-white/20 backdrop-blur-md rounded-3xl py-6 px-1.5 sm:py-10 sm:px-8 shadow-[0_12px_30px_rgba(0,0,0,0.08)] border border-white/40 text-center overflow-hidden">
          {/* Inner highlight */}
          <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/30 via-white/10 to-transparent pointer-events-none" />
          
          <div className="relative">
            <motion.div 
              className="mb-6 flex justify-center"
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Icon name="sprout" size="xl" animate />
            </motion.div>
            <h3 className="text-2xl font-bold text-gray-700 mb-3">
              Your Memory Garden Awaits
            </h3>
            <p className="text-gray-500 text-base mb-2">No memories planted yet...</p>
            <p className="text-gray-600 text-sm max-w-xs mx-auto leading-relaxed">
              Add your first precious memory above to watch your garden bloom
            </p>
            
            {/* Decorative elements */}
            <div className="flex justify-center space-x-5 mt-8 opacity-25" aria-hidden="true">
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0 }}
              >
                <Icon name="heart" size="lg" />
              </motion.div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.3 }}
              >
                <Icon name="star" size="lg" />
              </motion.div>
              <motion.div
                animate={{ y: [0, -8, 0] }}
                transition={{ duration: 2, repeat: Infinity, delay: 0.6 }}
              >
                <Icon name="sprout" size="lg" />
              </motion.div>
            </div>
          </div>
        </div>
      </motion.div>
    );
  }

  const isMobile = windowWidth < 768;

  return (
    <div className="w-full">
      {/* Floating stars background - decorative only */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
        {[...Array(5)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute opacity-20"
            style={{
              left: `${15 + Math.random() * 70}%`,
              top: `${10 + Math.random() * 80}%`,
            }}
            animate={{ 
              opacity: [0.15, 0.35, 0.15],
              scale: [0.9, 1.1, 0.9],
            }}
            transition={{
              duration: 3 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          >
            <Icon name="sparkle" size="sm" color="gratitude" />
          </motion.div>
        ))}
      </div>

      {/* Memory cards - 3-2-3-2 Layout with Floating Animation */}
      <div className="relative">
        <div className="w-full max-w-xl mx-auto px-1.5 sm:px-4">
          <div className="relative flex flex-col items-center gap-4 py-4">
            {(() => {
              // Organize memories into 3-2-3-2 pattern
              const rows = [];
              let currentIndex = 0;
              
              while (currentIndex < memories.length) {
                // First row: 3 items
                if (currentIndex < memories.length) {
                  rows.push({
                    type: 'three',
                    items: memories.slice(currentIndex, currentIndex + 3),
                    startIndex: currentIndex
                  });
                  currentIndex += 3;
                }
                
                // Second row: 2 items
                if (currentIndex < memories.length) {
                  rows.push({
                    type: 'two',
                    items: memories.slice(currentIndex, currentIndex + 2),
                    startIndex: currentIndex
                  });
                  currentIndex += 2;
                }
                
                // Third row: 3 items
                if (currentIndex < memories.length) {
                  rows.push({
                    type: 'three',
                    items: memories.slice(currentIndex, currentIndex + 3),
                    startIndex: currentIndex
                  });
                  currentIndex += 3;
                }
                
                // Fourth row: 2 items
                if (currentIndex < memories.length) {
                  rows.push({
                    type: 'two',
                    items: memories.slice(currentIndex, currentIndex + 2),
                    startIndex: currentIndex
                  });
                  currentIndex += 2;
                }
              }
              
              return rows.map((row, rowIndex) => (
                <div
                  key={rowIndex}
                  className={`flex justify-center items-center w-full ${
                    row.type === 'two' ? 'gap-6' : 'gap-4'
                  }`}
                >
                  {row.items.map((memory, itemIndex) => {
                    const globalIndex = row.startIndex + itemIndex;
                    return (
                      <div
                        key={memory.id || globalIndex}
                        className={`memory-bubble memory-bubble-${globalIndex % 10}`}
                        style={{
                          animationDelay: `${globalIndex * 0.2}s`
                        }}
                      >
                        <MemoryCard memory={memory} index={globalIndex} onCardClick={handleCardClick} />
                      </div>
                    );
                  })}
                </div>
              ));
            })()}
          </div>
        </div>
      </div>

      {/* Memory Detail Modal */}
      <MemoryDetailModal
        memory={selectedMemory}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onDeleteMemory={handleDeleteMemory}
        onEditMemory={handleEditMemory}
      />

      {/* Memory Edit Modal */}
      <MemoryEditModal
        memory={editingMemory}
        isOpen={isEditModalOpen}
        onClose={handleCloseEditModal}
        onSaveMemory={onUpdateMemory}
      />

      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={isConfirmModalOpen}
        onConfirm={handleConfirmDelete}
        onCancel={handleCancelDelete}
        message="Are you sure you want to delete this precious memory?"
      />
    </div>
  );
};

// Memory Detail Modal Component - Premium Glass Design
const MemoryDetailModal = ({ memory, isOpen, onClose, onDeleteMemory, onEditMemory }) => {
  if (!isOpen || !memory) return null;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleDelete = () => {
    onDeleteMemory(memory);
  };

  const handleEdit = () => {
    onEditMemory(memory);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white/90 backdrop-blur-md rounded-3xl w-full max-w-md max-h-[90vh] overflow-y-auto shadow-[0_16px_40px_rgba(0,0,0,0.10)] border border-white/30"
      >
        {/* Inner highlight */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none" />
        
        {/* Header */}
        <div className="relative py-4 px-1.5 sm:py-6 sm:px-5 pb-2 sm:pb-4">
          <motion.button
            onClick={onClose}
            className="absolute top-4 right-4 bg-white/60 hover:bg-white/80 backdrop-blur-sm rounded-full p-2.5 transition-colors duration-200 border border-white/40 shadow-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon name="close" size="sm" color="neutral" />
          </motion.button>
          <h3 className="text-xl font-bold text-gray-700 pr-12 flex items-center gap-2">
            <Icon name="heart" size="md" color="memories" />
            Precious Memory
          </h3>
        </div>

        {/* Memory Image */}
        <div className="relative px-1.5 sm:px-5 pb-4">
          <div className="relative rounded-2xl overflow-hidden shadow-lg">
            <img
              src={memory.image}
              alt="Memory"
              className="w-full h-56 sm:h-64 object-cover"
              onError={(e) => {
                e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 300"><rect width="400" height="300" fill="%23f0f0f0"/><text x="200" y="150" text-anchor="middle" dy=".3em" fill="%23999" font-size="48">📷</text></svg>';
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
          </div>
        </div>

        {/* Memory Details */}
        <div className="relative px-1.5 sm:px-5 pb-4 sm:pb-6">
          {/* Date */}
          {memory.date && (
            <div className="mb-4">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Date</h4>
              <p className="text-gray-700 font-medium flex items-center gap-2 text-sm">
                <Icon name="star" size="sm" color="bonds" />
                {formatDate(memory.date)}
              </p>
            </div>
          )}

          {/* Memory Text */}
          {memory.text && (
            <div className="mb-6">
              <h4 className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">Memory</h4>
              <div className="bg-white/50 backdrop-blur-sm rounded-2xl p-4 border border-white/40">
                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap text-sm">
                  {memory.text}
                </p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3 mb-4">
            {/* Edit Button */}
            <motion.button
              onClick={handleEdit}
              className="flex-1 bg-white/50 backdrop-blur-sm text-blue-600 px-4 py-3 rounded-2xl font-medium border border-blue-200/50 shadow-sm hover:bg-blue-50/50 transition-colors duration-200 flex items-center justify-center gap-2"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon name="settings" size="sm" color="#2563eb" />
              <span>Edit</span>
            </motion.button>
            
            {/* Delete Button */}
            <motion.button
              onClick={handleDelete}
              className="flex-1 bg-white/50 backdrop-blur-sm text-rose-600 px-4 py-3 rounded-2xl font-medium border border-rose-200/50 shadow-sm hover:bg-rose-50/50 transition-colors duration-200 flex items-center justify-center gap-2"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon name="close" size="sm" color="#e11d48" />
              <span>Delete</span>
            </motion.button>
          </div>

          {/* Close Button */}
          <motion.div
            whileHover={{ y: -2, scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <PastelButton
              onClick={onClose}
              color="lavender"
              className="w-full py-3 font-semibold"
            >
              Close
            </PastelButton>
          </motion.div>
        </div>
      </motion.div>
    </div>
  );
};

// Confirmation Modal Component - Premium Glass Design
const ConfirmationModal = ({ isOpen, onConfirm, onCancel, message }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div 
        initial={{ opacity: 0, scale: 0.95, y: 20 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: 20 }}
        className="relative bg-white/90 backdrop-blur-md rounded-3xl w-full max-w-sm shadow-[0_16px_40px_rgba(0,0,0,0.10)] border border-white/30"
      >
        {/* Inner highlight */}
        <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none" />
        
        {/* Header */}
        <div className="relative py-4 px-1.5 sm:py-6 sm:px-5 pb-2 sm:pb-4 text-center">
          <motion.button
            onClick={onCancel}
            className="absolute top-4 right-4 bg-white/60 hover:bg-white/80 backdrop-blur-sm rounded-full p-2 transition-colors duration-200 border border-white/40 shadow-sm"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Icon name="close" size="sm" color="neutral" />
          </motion.button>
          
          {/* Icon with glow */}
          <motion.div 
            className="inline-flex items-center justify-center w-14 h-14 bg-rose-100/60 rounded-full mb-4"
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', delay: 0.1 }}
          >
            <Icon name="teardrop" size="lg" color="#e11d48" />
          </motion.div>
          
          <h3 className="text-xl font-bold text-gray-700 mb-3">
            Confirm Deletion
          </h3>
          <p className="text-gray-600 leading-relaxed text-sm max-w-xs mx-auto">
            {message}
          </p>
        </div>

        {/* Action Buttons */}
        <div className="relative px-1.5 sm:px-5 pb-4 sm:pb-6">
          <div className="flex gap-3">
            {/* Cancel Button */}
            <motion.button
              onClick={onCancel}
              className="flex-1 bg-white/50 backdrop-blur-sm text-gray-600 px-4 py-3 rounded-2xl font-medium border border-white/40 shadow-sm hover:bg-white/70 transition-colors duration-200 flex items-center justify-center gap-2"
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              Cancel
            </motion.button>
            
            {/* Confirm Delete Button */}
            <motion.button
              onClick={onConfirm}
              className="flex-1 bg-gradient-to-r from-rose-400 to-pink-500 hover:from-rose-500 hover:to-pink-600 text-white px-4 py-3 rounded-2xl font-semibold shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
              whileHover={{ y: -2, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <Icon name="close" size="sm" color="#ffffff" />
              Delete
            </motion.button>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default MemoryGarden; 